import _ from 'lodash'
import * as THREE from 'three'
import Config from './../config'

import LinearDimension from './../objects/linearDimension'
import AngularDimension from './../objects/angularDimension'
import LeaderLineDimension from './../objects/leaderLineDimension'
import EditingLine from './../objects/editingLine'
import EditingCutout from './../objects/editingCutout'
import EditingItem from './../objects/editingItem'
import EditingPoint from './../objects/editingPoint'
import GlassDatumLine from '@glass-designer/model/glassDatumLine'
import GlassSegment from '@/model/glassSegment'

import GlassDesignerTypes from '../types'
import MathsUtils from '@/utils/mathsUtils'
import CircleHole from '../model/holes/circleHole'
import RectangleHole from '../model/holes/rectangleHole'
import CustomHole from '../model/holes/customHole'




/**
 * Dimension Helper
 * Creates / Updates the dimensions around shapes, the cutouts & holes
 * Attempts to use intelligence to make the dimension objects smart and efficient about placement to not overlap
 */
export default class DimensionHelper {
  constructor(editor) {
    this.editor = editor

    // Dimension pool
    // Used to reuse dimensions (and their WebGL resources)
    // Particular useful while dynamically editing
    this.dimensionPool = []
  }


  /**
   * Reset & release all dimensions
   */
  resetAllDimensions() {
    let dimensionList = this.editor.getObjectListOfType([LinearDimension, AngularDimension, LeaderLineDimension])
    this.releaseDimension(dimensionList)
  }


  /**
   * Updates dimensions for a glass shape
   * Attempts to reuse existing dimensions
   * Adds/Removes any dimensions not applicable
   */
  updateGlassShapeDimensions(glassShape) {
    let curDimList = this.getDimensionsForShape(glassShape)
    this.releaseDimension(curDimList)

    switch(this.editor.getViewLevel()) {
      case GlassDesignerTypes.EditorViewLevelTypes.normal:
      case GlassDesignerTypes.EditorViewLevelTypes.technical:
      case GlassDesignerTypes.EditorViewLevelTypes.cutouts:
        this._generateShapeNormalDimensions(glassShape)
        break

      case GlassDesignerTypes.EditorViewLevelTypes.cutting:
        this._generateShapeCuttingDimensions(glassShape)
        break
    }
  }


  /**
   * Generate the shape dimensions for normal viewing type
   *
   * @param {GlassShape} glassShape - The glass shape to generate dimensions for
   */
  _generateShapeNormalDimensions(glassShape) {
    // Calculate shape segment bounds
    let shapeBounds = glassShape.calculateSegmentBounds()

    // Calculate flags for the segments (like winding)
    let segmentFlags = this.calculateSegmentFlags(glassShape)

    let isViewLevelTechnical = this.editor.getViewLevel() == GlassDesignerTypes.EditorViewLevelTypes.technical
    let isViewLevelCutouts = this.editor.getViewLevel() == GlassDesignerTypes.EditorViewLevelTypes.cutouts

    // Circle/Oval shape have different rules for some dimensions
    let isShapeCircle = glassShape.getShapeType() == GlassDesignerTypes.LibraryItemShapeTypes.circle
    let isShapeOval = glassShape.getShapeType() == GlassDesignerTypes.LibraryItemShapeTypes.oval

    var segmentDimension = null

    var displaySegmentDimensions = true

    // Don't add individual segment dimensions for technical / cutouts mode
    // They get displayed in projected dimensions
    if(isViewLevelTechnical || isViewLevelCutouts)
      displaySegmentDimensions = false

    // Outline dimensions
    glassShape.segmentList.forEach((segment, segmentIndex) => {

      // Don't add segment dimensions for stock only sheets
      if(glassShape.isStockLinked())
        return

      // If the segment is part of a group, don't show individual segment dimensions until the group is opened
      // Entire segment must be part of the group
      if(glassShape.isSegmentInGroup(segment.id))
        return

      
      if(segment.type == GlassDesignerTypes.SegmentTypes.line) {
        if(displaySegmentDimensions) {
          let l = segment.getLength()

          // If the segment end point is in a group, display a special dimension to the group instead of segment
          let segEndGroup = glassShape.getSegmentEndLinkedGroup(segment.id)
          let segStartGroup = glassShape.getSegmentStartLinkedGroup(segment.id)
          if(segStartGroup || segEndGroup) {
            this._generateGroupLinkedSegmentDimension(glassShape, segment, segmentIndex, segStartGroup, segEndGroup)
            return
          }

          segmentDimension = this.createLinearDimension({
            dimType: GlassDesignerTypes.DimensionTypes.linearGlassSegmentDimension,
            glassShape: glassShape,
            glassSegmentIndex: segmentIndex,
            editable: glassShape.isItemEditable()
          })

          let dir = new THREE.Vector2()
          dir.subVectors(segment.endPosition, segment.startPosition)
          dir.normalize()

          segmentDimension.clearPoints()
          segmentDimension._isUserFixed = segment.isUserFixed
          segmentDimension.lineDirection = new THREE.Vector3(dir.x, dir.y, 0)
          segmentDimension.extendDirection = new THREE.Vector3(dir.y, -dir.x, 0)
          segmentDimension.displayPoint = null
          segmentDimension.isReadOnly = !glassShape.isItemEditable()
          segmentDimension.setHighlighted(segment.getSegmentSelected())

          // If the orientation is reversed for this segment, show the segment dimension on the opposite side
          if(segmentFlags[segmentIndex].hasAlternateOrientation) {
            segmentDimension.extendDirection.multiplyScalar(-1)
          }

          // If axis aligned, cast out to be part of the shape outline bounds
          // Ditching this idea. Too many complexities at the moment
          /*
          let boundsLine = this.projectDimNearestBoundsLine(shapeBounds, segment.startPosition, segment.endPosition)
          let axisAlignedTolerance = 0.025
          if(Math.max(Math.abs(dir.x), Math.abs(dir.y)) > 1.0 - axisAlignedTolerance && !isBoundsDiffLength) {
            
            let boundDir = new THREE.Vector2(boundsLine.endPosition.x - boundsLine.startPosition.x, boundsLine.endPosition.y - boundsLine.startPosition.y)
            boundDir.normalize()

            // TODO: Cast line from this point to the outline point
            // If it intersects the shape, don't cast it out to the outline & just use on the inside


            segmentDimension.lineDirection = new THREE.Vector3(boundDir.x, boundDir.y, 0)
            segmentDimension.extendDirection = new THREE.Vector3(boundDir.y, -boundDir.x, 0)          
            segmentDimension.displayPoint = new THREE.Vector3(boundsLine.startPosition.x, boundsLine.startPosition.y, 0)
          }*/

          segmentDimension.addPoint(new THREE.Vector3(segment.startPosition.x, segment.startPosition.y, 0))
          segmentDimension.addPoint(new THREE.Vector3(segment.endPosition.x, segment.endPosition.y, 0))
          segmentDimension.build()
        }


        // Angle dimension
        // Ignore if going from line to arc
        let prevSeg = glassShape.getSegmentForIndex(segmentIndex-1)
        let nextSeg = glassShape.getSegmentForIndex(segmentIndex+1)
        if(nextSeg.type != GlassDesignerTypes.SegmentTypes.arc) {
          var angleDimension = this.createAngularDimension({
            dimType: GlassDesignerTypes.DimensionTypes.angularGlassSegmentDimension,
            glassShape: glassShape,
            linkData: { segmentIndex: segmentIndex }
          })
          angleDimension.isReadOnly = true
          angleDimension.buildFromLines([
            {
              startPoint: new THREE.Vector3(segment.endPosition.x, segment.endPosition.y, 0),
              endPoint: new THREE.Vector3(segment.startPosition.x, segment.startPosition.y, 0)
            }, {
              startPoint: new THREE.Vector3(nextSeg.startPosition.x, nextSeg.startPosition.y, 0),
              endPoint: new THREE.Vector3(nextSeg.endPosition.x, nextSeg.endPosition.y, 0)
            }
          ], new THREE.Vector3(segment.endPosition.x, segment.endPosition.y, 0))

          // If it's a square angle dimension & the segment uses an end corner radius, just remove the dimension
          if(angleDimension.isSquareAngle && (segment.endCornerRadius > 1.0 || nextSeg.startCornerRadius > 1.0)) {
            this.releaseDimension(angleDimension)
          }
        }


        // Display corner radius leader lines in technical mode
        if(isViewLevelTechnical) {
          if(segment.startCornerRadius > 0.01) {
            let segAOutDir = segment.getOutDirection().negate()
            let segBOutDir = prevSeg.getOutDirection().negate()
            let radiusText = `R${Math.round(segment.startCornerRadius).toFixed(0)}`

            var cornerRadiusLeaderDimension = this.createLeaderLineDimension({
              dimType: GlassDesignerTypes.DimensionTypes.infoLineDimension,
              glassShape: glassShape,
              linkData: { segmentIndex: segmentIndex }
            })
            cornerRadiusLeaderDimension.isReadOnly = true
            cornerRadiusLeaderDimension.buildFromInfo({
              targetPoint: new THREE.Vector3(segment.startPosition.x, segment.startPosition.y, 0),
              direction: new THREE.Vector3(segAOutDir.x + segBOutDir.x, segAOutDir.y + segBOutDir.y, 0),
              text: radiusText
            })
          }
        }
      }
    })

    // Overall dimensions
    this.generateShapeOverallDimensions(glassShape, shapeBounds)

    // Projected dimensions
    if(!glassShape.isStockLinked() && (isViewLevelTechnical || isViewLevelCutouts) && !isShapeCircle && !isShapeOval)
      this.generateShapeProjectedDimensions(glassShape, shapeBounds)

    // Stock-only dimensions for split lines
    if(glassShape.isStockLinked())
      this.generateShapeStockSplitDimensions(glassShape)


    // Hole dimensions
    // Notes: If dragging holes in, the dimensions should be offset from a fixed location based on the original destination
    // So even if hole moved all the way closer to another edge, still keep dimension from original insert
    glassShape.getCenterCutoutList().forEach(hole => {
      let holeBounds = hole.calculateSegmentBounds()
      let anchorWorldPoint = hole.calculateAnchorWorldPoint()

      var segmentDimension = null
      // Hole size dimensions currently disabled
      // Looks too convoluted. Potentially in future maybe only display when zoomed in closer to the holes
      // Enabled in technical / cutouts view
      let showSizeDimensions = (isViewLevelTechnical || isViewLevelCutouts)

      if(showSizeDimensions && !(hole instanceof CircleHole)) {

        // Width dimension
        segmentDimension = this.createLinearDimension({
          dimType: GlassDesignerTypes.DimensionTypes.glassHoleSizeDimension,
          glassHole: hole,
          glassShape: glassShape,
          linkData: { shapeId: glassShape.id, holeId: hole.id }
        })

        segmentDimension.clearPoints()
        segmentDimension.isReadOnly = true
        segmentDimension.lineDirection = new THREE.Vector3(1, 0, 0)
        segmentDimension.extendDirection = new THREE.Vector3(0, -1, 0)
        segmentDimension.addPoint(new THREE.Vector3(holeBounds.min.x, holeBounds.min.y, 0))
        segmentDimension.addPoint(new THREE.Vector3(holeBounds.max.x, holeBounds.min.y, 0))
        segmentDimension.build()

        // Height dimension
        segmentDimension = this.createLinearDimension({
          dimType: GlassDesignerTypes.DimensionTypes.glassHoleSizeDimension,
          glassHole: hole,
          glassShape: glassShape,
          linkData: { shapeId: glassShape.id, holeId: hole.id }
        })

        segmentDimension.clearPoints()
        segmentDimension.isReadOnly = true
        segmentDimension.lineDirection = new THREE.Vector3(0, 1, 0)
        segmentDimension.extendDirection = new THREE.Vector3(1, 0, 0)
        segmentDimension.addPoint(new THREE.Vector3(holeBounds.max.x, holeBounds.min.y, 0))
        segmentDimension.addPoint(new THREE.Vector3(holeBounds.max.x, holeBounds.max.y, 0))
        segmentDimension.build()

        
        // Rectangle : Corner radius
        if((hole instanceof RectangleHole) && isViewLevelTechnical) {
          let diameterText = `R${Math.round(hole.getCornerRadius()).toFixed(0)}`
          let direction = new THREE.Vector3(1, 1, 0)
          direction.normalize()
          let targetPoint = new THREE.Vector3(holeBounds.max.x, holeBounds.max.y, 0)

          let holeDiameterLeaderDimension = this.createLeaderLineDimension({
            dimType: GlassDesignerTypes.DimensionTypes.infoLineDimension,
            glassShape: glassShape,
            linkData: { shapeId: glassShape.id, holeId: hole.id }
          })
          holeDiameterLeaderDimension.isReadOnly = true
          holeDiameterLeaderDimension.buildFromInfo({
            targetPoint: targetPoint,
            direction: direction,
            text: diameterText
          })

        }

        // Custom Hole : Corner radiuses
        if((hole instanceof CustomHole) && isViewLevelTechnical) {
          hole.segmentList.forEach((holeSegment, holeSegmentIndex) => {
            let holePrevSegment = hole.segmentList[holeSegmentIndex == 0 ? hole.segmentList.length-1 : holeSegmentIndex-1]

            if(holeSegment.startCornerRadius > 0.01) {
              let segAOutDir = holeSegment.getOutDirection()
              let segBOutDir = holePrevSegment.getOutDirection()
              let radiusText = `R${Math.round(holeSegment.startCornerRadius).toFixed(0)}`
  
              var cornerRadiusLeaderDimension = this.createLeaderLineDimension({
                dimType: GlassDesignerTypes.DimensionTypes.infoLineDimension,
                glassShape: glassShape,
                linkData: { shapeId: glassShape.id, holeId: hole.id, segmentIndex: holeSegmentIndex }
              })
              cornerRadiusLeaderDimension.isReadOnly = true
              cornerRadiusLeaderDimension.buildFromInfo({
                targetPoint: new THREE.Vector3(holeSegment.startPosition.x, holeSegment.startPosition.y, 0),
                direction: new THREE.Vector3(segAOutDir.x + segBOutDir.x, segAOutDir.y + segBOutDir.y, 0),
                text: radiusText
              })
            }
          })
        }
      }

      // Show diameter for circles
      if(showSizeDimensions && (hole instanceof CircleHole)) {
        let diameterText = `ø${Math.round(hole.radius * 2.0).toFixed(0)}`
        let direction = new THREE.Vector3(1, -1, 0)
        direction.normalize()
        let targetPoint = new THREE.Vector3((holeBounds.max.x + holeBounds.min.x) / 2.0 + (direction.x * hole.radius), (holeBounds.max.y + holeBounds.min.y) / 2.0 + (direction.y * hole.radius), 0)

        let holeDiameterLeaderDimension = this.createLeaderLineDimension({
          dimType: GlassDesignerTypes.DimensionTypes.infoLineDimension,
          glassShape: glassShape,
          linkData: { shapeId: glassShape.id, holeId: hole.id }
        })
        holeDiameterLeaderDimension.isReadOnly = true
        holeDiameterLeaderDimension.buildFromInfo({
          targetPoint: targetPoint,
          direction: new THREE.Vector3(1, -1, 0),
          text: diameterText
        })

      }

      // Base dimensions from the hole's datum links
      hole.datumLinks.forEach(datumLink => {
        var dirToHole = datumLink.calculateLinkDirection(this.editor)
        if(dirToHole == null)
          return

        segmentDimension = this.createLinearDimension({
          dimType: GlassDesignerTypes.DimensionTypes.glassHoleOffsetDimension,
          glassHole: hole,
          glassShape: glassShape,
          linkData: { shapeId: glassShape.id, datumLinkId: datumLink.id, holeId: hole.id }
        })
       
        var dirRight = new THREE.Vector2(dirToHole.y, -dirToHole.x)
        var holeEdge = anchorWorldPoint

        var datumOrigin = holeEdge.clone().addScaledVector(dirToHole, -Math.abs(datumLink.sourceOffsetDistance * datumLink.sourceOffsetDirection))

        // Auto hide the hole dimensions when zooming out to stop clutter
        // TODO: Change to a zoom level for all offset dimensions, regardless of size
        //segmentDimension.minSizeVisibleFactor = 100.0
        //segmentDimension.minSizeVisibleMax = 2000.0

        segmentDimension.clearPoints()
        segmentDimension.isReadOnly = (isViewLevelTechnical || isViewLevelCutouts)
        segmentDimension.lineDirection = new THREE.Vector3(dirToHole.x, dirToHole.y, 0)
        segmentDimension.extendDirection = new THREE.Vector3(dirRight.x, dirRight.y, 0)
        segmentDimension.setHighlighted(hole.getSelected())
        segmentDimension.addPoint(new THREE.Vector3(datumOrigin.x, datumOrigin.y, 0))
        segmentDimension.addPoint(new THREE.Vector3(holeEdge.x, holeEdge.y, 0))
        segmentDimension.build()
      })
    })

    // Datum Line Dimensions
    // Hidden for cutout view level
    glassShape.getDatumLineList().forEach(datumLine => {
      if(!datumLine.isDimensionDisplayed || isViewLevelCutouts)
        return

      var datumLineDimension = this.createLinearDimension({
        dimType: GlassDesignerTypes.DimensionTypes.glassDatumLineDimension,
        glassShape: glassShape,
        glassDatumLine: datumLine,
        editable: false
      })

      let dir = datumLine.getDirection()

      datumLineDimension.clearPoints()
      datumLineDimension._isUserFixed = false
      datumLineDimension.lineDirection = new THREE.Vector3(dir.x, dir.y, 0)
      datumLineDimension.extendDirection = new THREE.Vector3(dir.y, -dir.x, 0)
      datumLineDimension.displayPoint = null
      datumLineDimension.isReadOnly = true
      datumLineDimension.addPoint(new THREE.Vector3(datumLine.startPosition.x, datumLine.startPosition.y, 0))
      datumLineDimension.addPoint(new THREE.Vector3(datumLine.endPosition.x, datumLine.endPosition.y, 0))
      datumLineDimension.build()
    })
  }


  /**
   * Create a linear dimension for a segment that connects to a shape group
   * The linear dimension will extend to the edge/center (based on group settings)
   */
  _generateGroupLinkedSegmentDimension(glassShape, segment, segmentIndex, segStartGroup, segEndGroup) {
    let dir = new THREE.Vector2()
    dir.subVectors(segment.endPosition, segment.startPosition)
    dir.normalize()

    let startPosition = segment.startPosition.clone()
    let endPosition = segment.endPosition.clone()

    var dimLinkData = {}

    // Extend to center of group if required
    if(segStartGroup) {
      if(segStartGroup.cutoutMeasurementType == GlassDesignerTypes.CutoutMeasurementTypes.center) {
        let startGroupDir = dir.clone().negate()
        let groupRangeDist = segStartGroup.calculateGroupRangeInfoForDirection(startGroupDir, segment.startPosition)
        let startOffset = groupRangeDist.min + (groupRangeDist.max - groupRangeDist.min) / 2.0

        startPosition.addScaledVector(startGroupDir, startOffset)

        dimLinkData.startShapeGroupId = segStartGroup.id
        dimLinkData.startGroupOffset = startOffset
      }
    }
    if(segEndGroup) {
      if(segEndGroup.cutoutMeasurementType == GlassDesignerTypes.CutoutMeasurementTypes.center) {
        let groupRangeDist = segEndGroup.calculateGroupRangeInfoForDirection(dir, segment.endPosition)
        let endOffset = groupRangeDist.min + (groupRangeDist.max - groupRangeDist.min) / 2.0

        endPosition.addScaledVector(dir, endOffset)

        dimLinkData.endShapeGroupId = segEndGroup.id
        dimLinkData.endGroupOffset = endOffset
      }
    }

    let segmentDimension = this.createLinearDimension({
      dimType: GlassDesignerTypes.DimensionTypes.linearGlassSegmentDimension,
      glassShape: glassShape,
      glassSegmentIndex: segmentIndex,
      editable: glassShape.isItemEditable(),
      linkData: dimLinkData
    })

    segmentDimension.clearPoints()
    segmentDimension._isUserFixed = segment.isUserFixed
    segmentDimension.lineDirection = new THREE.Vector3(dir.x, dir.y, 0)
    segmentDimension.extendDirection = new THREE.Vector3(dir.y, -dir.x, 0)
    segmentDimension.displayPoint = null
    segmentDimension.isReadOnly = !glassShape.isItemEditable()

    segmentDimension.addPoint(new THREE.Vector3(startPosition.x, startPosition.y, 0))
    segmentDimension.addPoint(new THREE.Vector3(endPosition.x, endPosition.y, 0))
    segmentDimension.build()
  }


  /**
   * Generate the top & right overall dimensions for the glass shape
   * Does not add in dimensions if the shape itself is simple and the segment dimensions would already match the overall dimensions
   */
  generateShapeOverallDimensions(glassShape, shapeBounds) {
    // Overall dimensions only editable in normal mode & not stock linked panels
    let overallEditable = this.editor.getViewLevel() == GlassDesignerTypes.EditorViewLevelTypes.normal
    if(!glassShape.isItemEditable())
      overallEditable = false
    if(glassShape.isStockLinked())
      overallEditable = false


    // Top dimension
    var topOverallDimension = this.createLinearDimension({
      dimType: GlassDesignerTypes.DimensionTypes.linearGlassOverallDimension,
      glassShape: glassShape
    })

    topOverallDimension.clearPoints()
    topOverallDimension._isUserFixed = false
    topOverallDimension.lineDirection = new THREE.Vector3(1, 0, 0)
    topOverallDimension.extendDirection = new THREE.Vector3(0, -1, 0)
    topOverallDimension.displayPoint = null
    topOverallDimension.isReadOnly = !overallEditable
    topOverallDimension.addPoint(new THREE.Vector3(shapeBounds.min.x, shapeBounds.min.y, 0))
    topOverallDimension.addPoint(new THREE.Vector3(shapeBounds.max.x, shapeBounds.min.y, 0))
    topOverallDimension.build()


    // Right dimension
    var rightOverallDimension = this.createLinearDimension({
      dimType: GlassDesignerTypes.DimensionTypes.linearGlassOverallDimension,
      glassShape: glassShape
    })

    rightOverallDimension.clearPoints()
    rightOverallDimension._isUserFixed = false
    rightOverallDimension.lineDirection = new THREE.Vector3(0, 1, 0)
    rightOverallDimension.extendDirection = new THREE.Vector3(1, 0, 0)
    rightOverallDimension.displayPoint = null
    rightOverallDimension.isReadOnly = !overallEditable
    rightOverallDimension.addPoint(new THREE.Vector3(shapeBounds.max.x, shapeBounds.min.y, 0))
    rightOverallDimension.addPoint(new THREE.Vector3(shapeBounds.max.x, shapeBounds.max.y, 0))
    rightOverallDimension.build()
  }


  /**
   * Generate the projected dimensions for a glass shape
   * Shows the dimensions for all the points in the shape projected to the sides
   * 
   * @param {GlassShape} glassShape - The glass shape to generate dimensions for
   * @param {Box2} shapeBounds - Bounds of the shape
   */
   generateShapeProjectedDimensions(glassShape, shapeBounds) {
    var projectedPointList = []

    glassShape.segmentList.forEach(segment => {
      projectedPointList.push(segment.startPosition)
      projectedPointList.push(segment.endPosition)
    })

    // Projected lines
    var topKeyPoints = this._calculateKeyPoints(shapeBounds, projectedPointList, new THREE.Vector2(0, -1), -1)
    var rightKeyPoints = this._calculateKeyPoints(shapeBounds, projectedPointList, new THREE.Vector2(1, 0), -1)
    var bottomKeyPoints = this._calculateKeyPoints(shapeBounds, projectedPointList, new THREE.Vector2(0, 1), -1)
    var leftKeyPoints = this._calculateKeyPoints(shapeBounds, projectedPointList, new THREE.Vector2(-1, 0), -1)

    if(topKeyPoints.length > 2) {
      this._generateLinearDimensionsForPointList(glassShape, topKeyPoints, {
        dimType: GlassDesignerTypes.DimensionTypes.linearGlassProjectedDimension,
        _isUserFixed: false,
        lineDirection: new THREE.Vector2(1, 0),
        extendDirection: new THREE.Vector2(0, -1),
        isReadOnly: true
      })
    }
    if(rightKeyPoints.length > 2) {
      this._generateLinearDimensionsForPointList(glassShape, rightKeyPoints, {
        dimType: GlassDesignerTypes.DimensionTypes.linearGlassProjectedDimension,
        _isUserFixed: false,
        lineDirection: new THREE.Vector2(0, 1),
        extendDirection: new THREE.Vector2(1, 0),
        isReadOnly: true
      })
    }
    if(bottomKeyPoints.length > 2) {
      this._generateLinearDimensionsForPointList(glassShape, bottomKeyPoints, {
        dimType: GlassDesignerTypes.DimensionTypes.linearGlassProjectedDimension,
        _isUserFixed: false,
        lineDirection: new THREE.Vector2(-1, 0),
        extendDirection: new THREE.Vector2(0, 1),
        isReadOnly: true
      })
    }
    if(leftKeyPoints.length > 2) {
      this._generateLinearDimensionsForPointList(glassShape, leftKeyPoints, {
        dimType: GlassDesignerTypes.DimensionTypes.linearGlassProjectedDimension,
        _isUserFixed: false,
        lineDirection: new THREE.Vector2(0, -1),
        extendDirection: new THREE.Vector2(-1, 0),
        isReadOnly: true
      })
    }
  }


  /**
   * Generate the dimensions for a stock linked glass shape
   * Shows the dimensions for the splitting lines
   *
   * @param {GlassShape} glassShape - The glass shape to generate dimensions for
   */
  generateShapeStockSplitDimensions(glassShape) {
    var splitLineList = glassShape.getSplitLineList()
    var splitPointList = []

    // Total glass rectangle
    var outerBounds = glassShape.calculateSegmentBounds()
    splitLineList.forEach(splitLine => {
      splitPointList.push(splitLine.startPosition)
      splitPointList.push(splitLine.endPosition)
    })

    // Split lines lines
    var topKeyPoints = this._calculateKeyPoints(outerBounds, splitPointList, new THREE.Vector2(0, -1))
    var rightKeyPoints = this._calculateKeyPoints(outerBounds, splitPointList, new THREE.Vector2(1, 0))
    var bottomKeyPoints = this._calculateKeyPoints(outerBounds, splitPointList, new THREE.Vector2(0, 1))
    var leftKeyPoints = this._calculateKeyPoints(outerBounds, splitPointList, new THREE.Vector2(-1, 0))

    if(topKeyPoints.length > 2) {
      this._generateLinearDimensionsForPointList(glassShape, topKeyPoints, {
        dimType: GlassDesignerTypes.DimensionTypes.linearGlassStandardDimension,
        _isUserFixed: false,
        lineDirection: new THREE.Vector2(1, 0),
        extendDirection: new THREE.Vector2(0, -1),
        isReadOnly: true
      })
    }
    if(rightKeyPoints.length > 2) {
      this._generateLinearDimensionsForPointList(glassShape, rightKeyPoints, {
        dimType: GlassDesignerTypes.DimensionTypes.linearGlassStandardDimension,
        _isUserFixed: false,
        lineDirection: new THREE.Vector2(0, 1),
        extendDirection: new THREE.Vector2(1, 0),
        isReadOnly: true
      })
    }
    if(bottomKeyPoints.length > 2) {
      this._generateLinearDimensionsForPointList(glassShape, bottomKeyPoints, {
        dimType: GlassDesignerTypes.DimensionTypes.linearGlassStandardDimension,
        _isUserFixed: false,
        lineDirection: new THREE.Vector2(-1, 0),
        extendDirection: new THREE.Vector2(0, 1),
        isReadOnly: true
      })
    }
    if(leftKeyPoints.length > 2) {
      this._generateLinearDimensionsForPointList(glassShape, leftKeyPoints, {
        dimType: GlassDesignerTypes.DimensionTypes.linearGlassStandardDimension,
        _isUserFixed: false,
        lineDirection: new THREE.Vector2(0, -1),
        extendDirection: new THREE.Vector2(-1, 0),
        isReadOnly: true
      })
    }
  }
  


  /**
   * Generate the dimensions for a glass shape in cutting view type
   * Shows the dimensions for the cut lines & the cutting sheet
   * 
   * @param {GlassShape} glassShape - The glass shape to generate dimensions for
   */
  _generateShapeCuttingDimensions(glassShape) {
    var cuttingLineList = glassShape.calculateCuttingLineList()
    var cutPointList = []

    // Total glass rectangle
    // Exclude bounds cuts
    var outerBounds = glassShape.calculateSegmentBounds()
    cuttingLineList = cuttingLineList.filter(cutLine => !cutLine.isOnBounds)
    cuttingLineList.forEach(cutLine => {
      outerBounds.expandByPoint(cutLine.startPosition)
      outerBounds.expandByPoint(cutLine.endPosition)

      cutPointList.push(cutLine.startPosition)
      cutPointList.push(cutLine.endPosition)
    })

    // Cutting lines
    let nearPointMinDist = 8.0    // Give a fair bit of leeway for cutting intersections near the shape edge
    var topKeyPoints = this._calculateKeyPoints(outerBounds, cutPointList, new THREE.Vector2(0, -1), nearPointMinDist)
    var rightKeyPoints = this._calculateKeyPoints(outerBounds, cutPointList, new THREE.Vector2(1, 0), nearPointMinDist)
    var bottomKeyPoints = this._calculateKeyPoints(outerBounds, cutPointList, new THREE.Vector2(0, 1), nearPointMinDist)
    var leftKeyPoints = this._calculateKeyPoints(outerBounds, cutPointList, new THREE.Vector2(-1, 0), nearPointMinDist)

    if(topKeyPoints.length > 1) {
      this._generateLinearDimensionsForPointList(glassShape, topKeyPoints, {
        dimType: GlassDesignerTypes.DimensionTypes.linearGlassStandardDimension,
        _isUserFixed: false,
        lineDirection: new THREE.Vector2(1, 0),
        extendDirection: new THREE.Vector2(0, -1),
        isReadOnly: true
      })
    }
    if(rightKeyPoints.length > 1) {
      this._generateLinearDimensionsForPointList(glassShape, rightKeyPoints, {
        dimType: GlassDesignerTypes.DimensionTypes.linearGlassStandardDimension,
        _isUserFixed: false,
        lineDirection: new THREE.Vector2(0, 1),
        extendDirection: new THREE.Vector2(1, 0),
        isReadOnly: true
      })
    }
    if(bottomKeyPoints.length > 1) {
      this._generateLinearDimensionsForPointList(glassShape, bottomKeyPoints, {
        dimType: GlassDesignerTypes.DimensionTypes.linearGlassStandardDimension,
        _isUserFixed: false,
        lineDirection: new THREE.Vector2(-1, 0),
        extendDirection: new THREE.Vector2(0, 1),
        isReadOnly: true
      })
    }
    if(leftKeyPoints.length > 1) {
      this._generateLinearDimensionsForPointList(glassShape, leftKeyPoints, {
        dimType: GlassDesignerTypes.DimensionTypes.linearGlassStandardDimension,
        _isUserFixed: false,
        lineDirection: new THREE.Vector2(0, -1),
        extendDirection: new THREE.Vector2(-1, 0),
        isReadOnly: true
      })
    }

    // Overall dimensions for the source shape
    // Top dimension
    var topOverallDimension = this.createLinearDimension({
      dimType: GlassDesignerTypes.DimensionTypes.linearGlassOverallDimension,
      glassShape: glassShape
    })

    topOverallDimension.clearPoints()
    topOverallDimension._isUserFixed = false
    topOverallDimension.lineDirection = new THREE.Vector3(1, 0, 0)
    topOverallDimension.extendDirection = new THREE.Vector3(0, -1, 0)
    topOverallDimension.displayPoint = null
    topOverallDimension.isReadOnly = true
    topOverallDimension.addPoint(new THREE.Vector3(outerBounds.min.x, outerBounds.min.y, 0))
    topOverallDimension.addPoint(new THREE.Vector3(outerBounds.max.x, outerBounds.min.y, 0))
    topOverallDimension.build()


    // Right dimension
    var rightOverallDimension = this.createLinearDimension({
      dimType: GlassDesignerTypes.DimensionTypes.linearGlassOverallDimension,
      glassShape: glassShape
    })

    rightOverallDimension.clearPoints()
    rightOverallDimension._isUserFixed = false
    rightOverallDimension.lineDirection = new THREE.Vector3(0, 1, 0)
    rightOverallDimension.extendDirection = new THREE.Vector3(1, 0, 0)
    rightOverallDimension.displayPoint = null
    rightOverallDimension.isReadOnly = true
    rightOverallDimension.addPoint(new THREE.Vector3(outerBounds.max.x, outerBounds.min.y, 0))
    rightOverallDimension.addPoint(new THREE.Vector3(outerBounds.max.x, outerBounds.max.y, 0))
    rightOverallDimension.build()


    // Display angle dimensions on segments corners
    // Required by cutting to see angle vs square corners
    glassShape.segmentList.forEach((segment, segmentIndex) => {
      // Don't add segment dimensions for stock only sheets
      if(glassShape.isStockLinked())
        return
      
      if(segment.type == GlassDesignerTypes.SegmentTypes.line) {
        // Angle dimension
        // Ignore if going from line to arc
        let nextSeg = glassShape.getSegmentForIndex(segmentIndex+1)
        if(nextSeg.type != GlassDesignerTypes.SegmentTypes.arc) {
          var angleDimension = this.createAngularDimension({
            dimType: GlassDesignerTypes.DimensionTypes.angularGlassSegmentDimension,
            glassShape: glassShape,
            linkData: { segmentIndex: segmentIndex }
          })
          angleDimension.isReadOnly = true
          angleDimension.buildFromLines([
            {
              startPoint: new THREE.Vector3(segment.endPosition.x, segment.endPosition.y, 0),
              endPoint: new THREE.Vector3(segment.startPosition.x, segment.startPosition.y, 0)
            }, {
              startPoint: new THREE.Vector3(nextSeg.startPosition.x, nextSeg.startPosition.y, 0),
              endPoint: new THREE.Vector3(nextSeg.endPosition.x, nextSeg.endPosition.y, 0)
            }
          ], new THREE.Vector3(segment.endPosition.x, segment.endPosition.y, 0))

          // If it's a square angle dimension & the segment uses an end corner radius, just remove the dimension
          if(angleDimension.isSquareAngle && (segment.endCornerRadius > 1.0 || nextSeg.startCornerRadius > 1.0)) {
            this.releaseDimension(angleDimension)
          }
        }
      }
    })
  }


  /**
   * Get the list of dimensions for a shape
   */
  getDimensionsForShape(glassShape) {
    let dimensionList = this.editor.getObjectListOfType([LinearDimension, AngularDimension, LeaderLineDimension])

    return dimensionList.filter(dimension => {
      return dimension.glassShape && dimension.glassShape == glassShape
    })
  }


  /**
   * Get the list of dimensions given a dimension type(s)
   */
  getDimensionsOfDimType(dimTypes) {
    let dimensionList = this.editor.getObjectListOfType([LinearDimension, AngularDimension, LeaderLineDimension])

    return dimensionList.filter(dimension => {

      if(_.isArray(dimTypes)) {
        return dimTypes.indexOf(dimension.dimType) != -1
      } else {
        return dimension.dimType == dimTypes
      }
    })
  }


  /**
   * Project a dimension segment onto the nearest outside outline of the shape
   * Assumes the dimension segment is axis aligned
   * 
   * Returns an object containing the startPosition, endPosition properties projected onto the shape outline
   */
  projectDimNearestBoundsLine(shapeBounds, startPosition, endPosition) {
    var projectedDim = {
      startPosition: startPosition,
      endPosition: endPosition,
      length: 0
    }

    var boundsMidPoint = new THREE.Vector2()
    shapeBounds.getCenter(boundsMidPoint)

    let dimLineDir = new THREE.Vector2(endPosition.x - startPosition.x, endPosition.y - startPosition.y)
    dimLineDir.normalize()

    // Note the direction of the bounds line based on clockwise winding around the shape

    var closestBoundsStart = null
    var closestBoundsEnd = null
    if(Math.abs(dimLineDir.x) > Math.abs(dimLineDir.y)) {
      // Top/Bottom bounds
      if(startPosition.y < boundsMidPoint.y) {
        closestBoundsStart = new THREE.Vector3(shapeBounds.min.x, shapeBounds.min.y, 0)
        closestBoundsEnd = new THREE.Vector3(shapeBounds.max.x, shapeBounds.min.y, 0)
      } else {
        closestBoundsStart = new THREE.Vector3(shapeBounds.max.x, shapeBounds.max.y, 0)
        closestBoundsEnd = new THREE.Vector3(shapeBounds.min.x, shapeBounds.max.y, 0)
      }

    } else {
      // Left/Right bounds
      if(startPosition.x < boundsMidPoint.x) {
        closestBoundsStart = new THREE.Vector3(shapeBounds.min.x, shapeBounds.max.y, 0)
        closestBoundsEnd = new THREE.Vector3(shapeBounds.min.x, shapeBounds.min.y, 0)
      } else {
        closestBoundsStart = new THREE.Vector3(shapeBounds.max.x, shapeBounds.min.y, 0)
        closestBoundsEnd = new THREE.Vector3(shapeBounds.max.x, shapeBounds.max.y, 0)
      }
    }

    // Project to the bounds line
    let boundsLineDir = new THREE.Vector2(closestBoundsEnd.x - closestBoundsStart.x, closestBoundsEnd.y - closestBoundsStart.y)
    boundsLineDir.normalize()

    let extendPlane = new THREE.Plane()
    extendPlane.setFromNormalAndCoplanarPoint(new THREE.Vector3(boundsLineDir.y, -boundsLineDir.x, 0), closestBoundsStart)

    var outStart = new THREE.Vector3()
    var outEnd = new THREE.Vector3()
    extendPlane.projectPoint(closestBoundsStart, outStart)
    extendPlane.projectPoint(closestBoundsEnd, outEnd)

    projectedDim.startPosition = new THREE.Vector2(outStart.x, outStart.y)
    projectedDim.endPosition = new THREE.Vector2(outEnd.x, outEnd.y)
    projectedDim.length = projectedDim.endPosition.distanceTo(projectedDim.startPosition)


    return projectedDim
  }


  /**
   * Calculate the segment flags for a shape in prep for auto-dimensioning
   * Called internally via updateGlassShapeDimensions()
   */
  calculateSegmentFlags(glassShape) {
    let testDistance = 10

    var segAStart = new THREE.Vector2(0, 0)
    var segAEnd = new THREE.Vector2(0, 0)
    var segBStart = new THREE.Vector2(0, 0)
    var segBEnd = new THREE.Vector2(0, 0)

    // Note: Doesn't handle arcs
    var segmentFlags = glassShape.segmentList.map(seg => {
      return {
        hasAlternateOrientation: false
      }
    })

    glassShape.segmentList.forEach((segment, segmentIndex) => {
      if(segment.type == GlassDesignerTypes.SegmentTypes.arc)
        return

      let nextIndex = (segmentIndex + 1) % glassShape.segmentList.length
      let nextSegment = glassShape.segmentList[nextIndex]

      if(nextSegment.type == GlassDesignerTypes.SegmentTypes.arc)
        return

      // If this & next dimension extended out would intersect, flip the smaller dimension
      var segAOut = segment.getOutDirection()
      var segBOut = nextSegment.getOutDirection()

      segAStart.copy(segment.startPosition)
      segAStart.addScaledVector(segAOut, testDistance * (segmentFlags[segmentIndex].hasAlternateOrientation ? -1 : 1))
      segAEnd.copy(segment.endPosition)
      segAEnd.addScaledVector(segAOut, testDistance * (segmentFlags[segmentIndex].hasAlternateOrientation ? -1 : 1))

      segBStart.copy(nextSegment.startPosition)
      segBStart.addScaledVector(segBOut, testDistance * (segmentFlags[nextIndex].hasAlternateOrientation ? -1 : 1))
      segBEnd.copy(nextSegment.endPosition)
      segBEnd.addScaledVector(segBOut, testDistance * (segmentFlags[nextIndex].hasAlternateOrientation ? -1 : 1))


      if(MathsUtils.checkSegmentSegmentIntersection(segAStart, segAEnd, segBStart, segBEnd, null, testDistance * 0.5)) {
        // Flip the smaller of the two dimensions 
        if(segment.getLength() < nextSegment.getLength())
          segmentFlags[segmentIndex].hasAlternateOrientation = !segmentFlags[segmentIndex].hasAlternateOrientation
        else
          segmentFlags[nextIndex].hasAlternateOrientation = !segmentFlags[nextIndex].hasAlternateOrientation
      }
    })

    return segmentFlags
  }


  /**
   * Reset the editing dimensions
   * All dimensions associated with editing objects get released into the dimension pool
   */
  resetEditingDimensions() {
    let curDimensions = this.getDimensionsOfDimType([
      GlassDesignerTypes.DimensionTypes.editingLineDimension,
      GlassDesignerTypes.DimensionTypes.editingCutoutDimension,
      GlassDesignerTypes.DimensionTypes.editingPointDimension
    ])

    // Release dimensions
    this.releaseDimension(curDimensions)
  }


  /**
   * Update dimensions for a shape & editing objects
   * Used for tools using editing objects (eg, editing line) that involve dimensions between shape and editing
   */
  updateToolGlassShapeDimensions(glassShape, editingObjectList) {
    this.resetEditingDimensions()

    // Minimum arc height for editing lines to be used perpendicular to the arc
    // Otherwise the dimensions will just be the same as straight lines
    var minEditingLineArcHeight = 40

    if(!glassShape)
      return

    editingObjectList.forEach(editingObj => {
      if(editingObj instanceof EditingLine) {
        if(editingObj.linkedStartObj) {
          let startPoint = new THREE.Vector2()
          let endPoint = new THREE.Vector2()

          let dimEditingStartPoint = editingObj.startPoint.clone()

          let linkDataA = { glassShapeId: glassShape.id, isStartLine: true, refAngle: 0, refPoint: new THREE.Vector2(), refDir: new THREE.Vector2() }
          let linkDataB = { glassShapeId: glassShape.id, isStartLine: true, refAngle: 0, refPoint: new THREE.Vector2(), refDir: new THREE.Vector2() }
          if(editingObj.linkedStartObj instanceof GlassSegment) {
            let segment = glassShape.getSegmentById(editingObj.linkedStartObj.id)
            if(segment) {
              linkDataA.refPoint.set(segment.startPosition.x, segment.startPosition.y)
              linkDataA.refDir.copy(segment.getDirection())
              linkDataA.refAngle = segment.getAngle()
              linkDataB.refPoint.set(segment.endPosition.x, segment.endPosition.y)
              linkDataB.refDir.copy(segment.getDirection().clone().negate())
              linkDataB.refAngle = segment.getAngle() + Math.PI

              // If an arc, handle it differently so it's perpendicular to the arc
              // Only do for large arcs, otherwise the dimensions will be incorrect for shallow arcs
              if(editingObj.linkedStartObj.type == GlassDesignerTypes.SegmentTypes.arc) {
                if(editingObj.linkedStartObj.calculateArcHeight() > minEditingLineArcHeight) {
                  let segOutDir = segment.getOutDirection()
                  let dist = MathsUtils.calculateDistanceToPlane2D(segOutDir, segment.startPosition, editingObj.startPoint)

                  linkDataA.refDir.set(segOutDir.x, segOutDir.y)
                  dimEditingStartPoint.set(segment.startPosition.x + segOutDir.x * dist, segment.startPosition.y + segOutDir.y * dist)

                  // Flag as perpendicular to arc in the linkData so the editing line can trace to it
                  linkDataA.isPerpendicularToArc = true

                  // Disable secondary dimension
                  linkDataB = null
                }
              }

              startPoint.copy(segment.startPosition)
              endPoint.copy(segment.endPosition)
            }
          }
          if(editingObj.linkedStartObj instanceof GlassDatumLine) {
            let datum = glassShape.getDatumLineById(editingObj.linkedStartObj.id)
            if(datum) {
              linkDataA.refPoint.set(datum.startPosition.x, datum.startPosition.y)
              linkDataA.refDir.copy(datum.getDirection())
              linkDataA.refAngle = datum.getAngle()
              linkDataB.refPoint.set(datum.endPosition.x, datum.endPosition.y)
              linkDataB.refDir.copy(datum.getDirection().clone().negate())
              linkDataB.refAngle = datum.getAngle() + Math.PI

              startPoint.copy(datum.startPosition)
              endPoint.copy(datum.endPosition)
            }
          }

          if(linkDataA) {
            let segmentDimension1 = this.createLinearDimension({
              dimType: GlassDesignerTypes.DimensionTypes.editingLineDimension,
              glassShape: glassShape,
              editable: true,
              linkData: linkDataA
            })  
            segmentDimension1.buildFromPoints([
              new THREE.Vector3(linkDataA.refPoint.x, linkDataA.refPoint.y, 0),
              new THREE.Vector3(dimEditingStartPoint.x, dimEditingStartPoint.y, 0)
            ])
          }

          if(linkDataB) {
            let segmentDimension2 = this.createLinearDimension({
              dimType: GlassDesignerTypes.DimensionTypes.editingLineDimension,
              glassShape: glassShape,
              editable: true,
              linkData: linkDataB
            })  
            segmentDimension2.buildFromPoints([
              new THREE.Vector3(dimEditingStartPoint.x, dimEditingStartPoint.y, 0),
              new THREE.Vector3(linkDataB.refPoint.x, linkDataB.refPoint.y, 0)
            ])
          }

          // Create an angle dimension
          if(editingObj.useAngles) {
            let angleDimension1 = this.createAngularDimension({
              dimType: GlassDesignerTypes.DimensionTypes.editingLineDimension,
              glassShape: glassShape,
              editable: true,
              squareAngleDisabled: true,
              linkData: linkDataA
            })
            angleDimension1.buildFromLines([
                {
                  startPoint: new THREE.Vector3(startPoint.x, startPoint.y, 0),
                  endPoint: new THREE.Vector3(endPoint.x, endPoint.y, 0),
                  isFlipped: editingObj.flipStartAngle
                }, {
                  startPoint: new THREE.Vector3(editingObj.startPoint.x, editingObj.startPoint.y, 0),
                  endPoint: new THREE.Vector3(editingObj.endPoint.x, editingObj.endPoint.y, 0)
                }
              ],
              new THREE.Vector3(editingObj.startPoint.x, editingObj.startPoint.y, 0),
              editingObj.startAngleFixed
            )
          }
        }

        if(editingObj.linkedEndObj) {
          let startPoint = new THREE.Vector2()
          let endPoint = new THREE.Vector2()

          let dimEditingEndPoint = editingObj.endPoint.clone()

          let linkDataA = { glassShapeId: glassShape.id, isStartLine: false, refAngle: 0, refPoint: new THREE.Vector2(), refDir: new THREE.Vector2() }
          let linkDataB = { glassShapeId: glassShape.id, isStartLine: false, refAngle: 0, refPoint: new THREE.Vector2(), refDir: new THREE.Vector2() }
          if(editingObj.linkedEndObj instanceof GlassSegment) {
            let segment = glassShape.getSegmentById(editingObj.linkedEndObj.id)
            if(segment) {
              linkDataA.refPoint.set(segment.startPosition.x, segment.startPosition.y)
              linkDataA.refDir.copy(segment.getDirection())
              linkDataA.refAngle = segment.getAngle()
              linkDataB.refPoint.set(segment.endPosition.x, segment.endPosition.y)
              linkDataB.refDir.copy(segment.getDirection().clone().negate())
              linkDataB.refAngle = segment.getAngle() + Math.PI

              // If an arc, handle it differently so it's perpendicular to the arc
              // Only do for large arcs, otherwise the dimensions will be incorrect for shallow arcs
              if(editingObj.linkedEndObj.type == GlassDesignerTypes.SegmentTypes.arc) {
                if(editingObj.linkedEndObj.calculateArcHeight() > minEditingLineArcHeight) {
                  let segOutDir = segment.getOutDirection()
                  let dist = MathsUtils.calculateDistanceToPlane2D(segOutDir, segment.endPosition, editingObj.endPoint)

                  linkDataB.refDir.set(segOutDir.x, segOutDir.y)
                  dimEditingEndPoint.set(segment.endPosition.x + segOutDir.x * dist, segment.endPosition.y + segOutDir.y * dist)

                  // Flag as perpendicular to arc in the linkData so the editing line can trace to it
                  linkDataB.isPerpendicularToArc = true

                  // Disable secondary dimension
                  linkDataA = null
                }
              }

              startPoint.copy(segment.startPosition)
              endPoint.copy(segment.endPosition)
            }
          }
          if(editingObj.linkedEndObj instanceof GlassDatumLine) {
            let datum = glassShape.getDatumLineById(editingObj.linkedEndObj.id)
            if(datum) {
              linkDataA.refPoint.set(datum.startPosition.x, datum.startPosition.y)
              linkDataA.refDir.copy(datum.getDirection())
              linkDataA.refAngle = datum.getAngle()
              linkDataB.refPoint.set(datum.endPosition.x, datum.endPosition.y)
              linkDataB.refDir.copy(datum.getDirection().clone().negate())
              linkDataB.refAngle = datum.getAngle() + Math.PI

              startPoint.copy(datum.startPosition)
              endPoint.copy(datum.endPosition)
            }
          }

          if(linkDataA) {
            let segmentDimension1 = this.createLinearDimension({
              dimType: GlassDesignerTypes.DimensionTypes.editingLineDimension,
              glassShape: glassShape,
              editable: true,
              linkData: linkDataA
            })  
            segmentDimension1.buildFromPoints([
              new THREE.Vector3(linkDataA.refPoint.x, linkDataA.refPoint.y, 0),
              new THREE.Vector3(dimEditingEndPoint.x, dimEditingEndPoint.y, 0)
            ])
          }

          if(linkDataB) {
            let segmentDimension2 = this.createLinearDimension({
              dimType: GlassDesignerTypes.DimensionTypes.editingLineDimension,
              glassShape: glassShape,
              editable: true,
              linkData: linkDataB
            })  
            segmentDimension2.buildFromPoints([
              new THREE.Vector3(dimEditingEndPoint.x, dimEditingEndPoint.y, 0),
              new THREE.Vector3(linkDataB.refPoint.x, linkDataB.refPoint.y, 0)
            ])
          }

          // Create an angle dimension
          if(editingObj.useAngles) {
            let angleDimension1 = this.createAngularDimension({
              dimType: GlassDesignerTypes.DimensionTypes.editingLineDimension,
              glassShape: glassShape,
              editable: true,
              squareAngleDisabled: true,
              linkData: linkDataA
            })
            angleDimension1.buildFromLines([
                {
                  startPoint: new THREE.Vector3(startPoint.x, startPoint.y, 0),
                  endPoint: new THREE.Vector3(endPoint.x, endPoint.y, 0),
                  isFlipped: editingObj.flipEndAngle
                }, {
                  startPoint: new THREE.Vector3(editingObj.endPoint.x, editingObj.endPoint.y, 0),
                  endPoint: new THREE.Vector3(editingObj.startPoint.x, editingObj.startPoint.y, 0)
                }
              ],
              new THREE.Vector3(editingObj.endPoint.x, editingObj.endPoint.y, 0),
              editingObj.endAngleFixed
            )
          }
        }
      }

      if(editingObj instanceof EditingCutout || editingObj instanceof EditingItem || editingObj instanceof EditingPoint) {
        editingObj.datumLinks.forEach(datumLink => {
          var dirToItem = datumLink.calculateLinkDirection(this.editor)
          if(dirToItem == null)
            return

          var dimType = GlassDesignerTypes.DimensionTypes.editingCutoutDimension
          if(editingObj instanceof EditingPoint)
            dimType = GlassDesignerTypes.DimensionTypes.editingPointDimension
  
          var linearDimension = this.createLinearDimension({
            dimType: dimType,
            linkData: { datumLinkId: datumLink.id }
          })
            
          var dirRight = new THREE.Vector2(dirToItem.y, -dirToItem.x)
          var editingPos = new THREE.Vector2(editingObj.position.x, editingObj.position.y)

          if(datumLink.hasSourcePoint()) {
            editingPos = datumLink.calculateSourcePosition(this.editor)
            dirToItem.negate()
          }

          var itemEdge = editingPos

          // Flip extend direction for segment end datum link
          if(datumLink.sourceDatumType == GlassDesignerTypes.DatumTypes.glassSegmentEnd)
            dirRight.negate()
  
          var datumOrigin = itemEdge.clone().addScaledVector(dirToItem, -Math.abs(datumLink.sourceOffsetDistance * datumLink.sourceOffsetDirection))
  
          linearDimension.clearPoints()
          linearDimension._isUserFixed = datumLink.isFixedDatumLink
          linearDimension.lineDirection = new THREE.Vector3(dirToItem.x, dirToItem.y, 0)
          linearDimension.extendDirection = new THREE.Vector3(dirRight.x, dirRight.y, 0)
          linearDimension.addPoint(new THREE.Vector3(datumOrigin.x, datumOrigin.y, 0))
          linearDimension.addPoint(new THREE.Vector3(itemEdge.x, itemEdge.y, 0))
          linearDimension.build()
        })
      }
    })
  }


  /**
   * Calculate key points for a side of a rectangle defined by the bounds
   * 
   * Returns an array of Vector2 objects for each key point on the side, including start/end points of the bounds
   */
  _calculateKeyPoints(bounds, pointList, sideDirection, nearPointMinDist = 2.0) {
    var keyPoints = []
    var center = bounds.getCenter(new THREE.Vector2())
    var pointPlanePos = new THREE.Vector2()

    if(sideDirection.x < -0.5) {
      keyPoints.push(new THREE.Vector2(bounds.min.x, bounds.min.y))
      keyPoints.push(new THREE.Vector2(bounds.min.x, bounds.max.y))
    } else if(sideDirection.x > 0.5) {
      keyPoints.push(new THREE.Vector2(bounds.max.x, bounds.min.y))
      keyPoints.push(new THREE.Vector2(bounds.max.x, bounds.max.y))
    }
    if(sideDirection.y < -0.5) {
      keyPoints.push(new THREE.Vector2(bounds.min.x, bounds.min.y))
      keyPoints.push(new THREE.Vector2(bounds.max.x, bounds.min.y))
    } else if(sideDirection.y > 0.5) {
      keyPoints.push(new THREE.Vector2(bounds.min.x, bounds.max.y))
      keyPoints.push(new THREE.Vector2(bounds.max.x, bounds.max.y))
    }

    pointPlanePos.copy(keyPoints[0])

    // Add points on the correct side
    pointList.forEach(point => {
      let dist = MathsUtils.calculateDistanceToPlane2D(sideDirection, center, point)
      if(dist > 0) {
        // Project point to plane
        // If too far away from the bounds edge, just ignore it. Don't display inner points
        let projectedPoint = MathsUtils.project2DPointOnPlane(point, sideDirection, pointPlanePos)

        if(nearPointMinDist >= 0.0 && projectedPoint.distanceTo(point) > nearPointMinDist)
          return

        // If point not already in keypoints list, add it
        if(!MathsUtils.pointInList(keyPoints, projectedPoint, 0.1))
          keyPoints.push(projectedPoint)
      }
    })


    // Sort by direction
    keyPoints = MathsUtils.sort2DPointListByDirection(keyPoints, new THREE.Vector2(sideDirection.y, -sideDirection.x))

    // Remove duplicates
    keyPoints = MathsUtils.removeConsecutive2DPointDuplicates(keyPoints)

    return keyPoints
  }


  /**
   * Generate linear dimensions for a list of points
   */
  _generateLinearDimensionsForPointList(glassShape, pointList, dimensionInfo) {
    if(pointList.length < 2)
      return

    for(var i=0; i<pointList.length-1; i++) {      

      var dimension = this.createLinearDimension({
        dimType: dimensionInfo.dimType,
        glassShape: glassShape
      })
  
      dimension.clearPoints()
      dimension.dimType = dimensionInfo.dimType
      dimension._isUserFixed = dimensionInfo._isUserFixed || false
      dimension.lineDirection = new THREE.Vector3(dimensionInfo.lineDirection.x, dimensionInfo.lineDirection.y, 0)
      dimension.extendDirection = new THREE.Vector3(dimensionInfo.extendDirection.x, dimensionInfo.extendDirection.y, 0)
      dimension.displayPoint = null
      dimension.isReadOnly = !!dimensionInfo.isReadOnly
      dimension.addPoint(new THREE.Vector3(pointList[i].x, pointList[i].y, 0))
      dimension.addPoint(new THREE.Vector3(pointList[i+1].x, pointList[i+1].y, 0))
      dimension.build()
    }
  }


  /**
   * Find an active dimension using dimType & linkData fields as filters
   */
  findDimensionByLinkData(dimType, linkDataFilter) {
    if(!linkDataFilter)
      return null

    let dimensions = this.editor.getObjectListOfType([LinearDimension, AngularDimension, LeaderLineDimension])

    let linkDataKeys = Object.keys(linkDataFilter || {})

    return dimensions.find(dimension => {
      // Ignore released dimensions
      if(this.dimensionPool.find(poolDim => poolDim.id == dimension.id) != null)
        return false
      if(dimension.dimType != dimType)
        return false

      if(!dimension.linkData && linkDataKeys.length > 0)
        return false

      // All link data keys in the filter must match the dimension
      let linkMatchCount = linkDataKeys.filter(key => dimension.linkData[key] == linkDataFilter[key]).length
      return linkMatchCount == linkDataKeys.length
    })
  }




  /**
   * Create a new linear dimension
   * Attempts to reuse an existing dimension released into the dimension pool
   * Otherwise creates a new dimension
   */
  createLinearDimension(options = {}) {
    var existingDimensionIndex = this.dimensionPool.findIndex(poolDim => {
      return (poolDim instanceof LinearDimension)
    })

    if(existingDimensionIndex != -1) {
      // Remove from the pool & setup for reuse
      var existingDimension = this.dimensionPool[existingDimensionIndex]     
      this.dimensionPool.splice(existingDimensionIndex, 1)

      existingDimension.clearPoints()
      existingDimension.dimType = options.dimType
      existingDimension.glassSegmentIndex = options.glassSegmentIndex
      existingDimension.glassShape = options.glassShape
      existingDimension.glassHole = options.glassHole
      existingDimension.glassDatumLine = options.glassDatumLine
      existingDimension.linkData = options.linkData
      existingDimension.listenMouseEvents = !!options.editable
      existingDimension.isReleasedDimension = false
      existingDimension.squareAngleDisabled = !!options.squareAngleDisabled
      existingDimension.reset()
      existingDimension.setVisible(true)

      return existingDimension
    }

    // Create a new dimension
    var newDimension = new LinearDimension(this.editor, options || {})
    newDimension.reset()
    return newDimension
  }


  /**
   * Create a new angular dimension
   * Attempts to reuse an existing dimension released into the dimension pool
   * Otherwise creates a new dimension
   */
  createAngularDimension(options = {}) {
    var existingDimensionIndex = this.dimensionPool.findIndex(poolDim => {
      return (poolDim instanceof AngularDimension)
    })

    if(existingDimensionIndex != -1) {
      // Remove from the pool & setup for reuse
      var existingDimension = this.dimensionPool[existingDimensionIndex]     
      this.dimensionPool.splice(existingDimensionIndex, 1)

      existingDimension.dimType = options.dimType
      existingDimension.glassShape = options.glassShape
      existingDimension.glassHole = options.glassHole
      existingDimension.linkData = options.linkData
      existingDimension.listenMouseEvents = !!options.editable
      existingDimension.squareAngleDisabled = !!options.squareAngleDisabled
      existingDimension.isReleasedDimension = false
      existingDimension.reset()
      existingDimension.setVisible(true)

      return existingDimension
    }

    // Create a new dimension
    var newDimension = new AngularDimension(this.editor, options || {})
    newDimension.reset()
    return newDimension
  }


  /**
   * Create a new leader line dimension
   * Attempts to reuse an existing dimension released into the dimension pool
   * Otherwise creates a new dimension
   */
  createLeaderLineDimension(options = {}) {
    var existingDimensionIndex = this.dimensionPool.findIndex(poolDim => {
      return (poolDim instanceof LeaderLineDimension)
    })

    if(existingDimensionIndex != -1) {
      // Remove from the pool & setup for reuse
      var existingDimension = this.dimensionPool[existingDimensionIndex]     
      this.dimensionPool.splice(existingDimensionIndex, 1)

      existingDimension.clearPoints()
      existingDimension.dimType = options.dimType
      existingDimension.glassShape = options.glassShape
      existingDimension.linkData = options.linkData
      existingDimension.listenMouseEvents = !!options.editable
      existingDimension.isReleasedDimension = false
      existingDimension.reset()
      existingDimension.setVisible(true)

      return existingDimension
    }

    // Create a new dimension
    var newDimension = new LeaderLineDimension(this.editor, options || {})
    newDimension.reset()
    return newDimension
  }


  /**
   * Release a single (or array of dimensions) back into the dimension pool for reusing
   */
  releaseDimension(dimension) {
    if(_.isArray(dimension)) {
      dimension.forEach(dim => {
        this.releaseDimension(dim)
      })
    } else {

      // Ignore if already in the pool
      if(this.dimensionPool.find(poolDim => poolDim.id == dimension.id) != null)
        return

      dimension.setVisible(false)

      // Clear settings
      dimension.glassShape = null
      dimension.glassHole = null
      dimension.linkData = null
      dimension.dimType = null
      dimension.glassSegmentIndex = 0
      dimension.isReleasedDimension = true

      this.dimensionPool.push(dimension)
    }
  }
  
}